import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

import { tpaColorName } from '../../../utils/color';

export type BadgeStylesParams = {
  badgeFont: StyleParamType.Font;
  badgeColor: StyleParamType.Color;
  badgeBackgroundColor: StyleParamType.Color;
  badgeBorderColor: StyleParamType.Color;
  badgeBorderWidth: StyleParamType.Number;
  badgeCornerRadiusTopLeft: StyleParamType.Number;
  badgeCornerRadiusTopRight: StyleParamType.Number;
  badgeCornerRadiusBottomLeft: StyleParamType.Number;
  badgeCornerRadiusBottomRight: StyleParamType.Number;
  badgeCornerRadiusLinked: StyleParamType.Number;
};

export const badgeStylesParams = createStylesParams<BadgeStylesParams>({
  badgeFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: {
        description:
          'Font for badge label chips shown on menu items (e.g. "Vegan", "Spicy", "New")',
      },
    },
  },
  badgeColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-paragraph-2-color'), 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: {
        description: 'Text color of badge label chips on menu items',
      },
    },
  },
  badgeBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(
      tpaColorName('wst-primary-background-color'),
      1,
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 1,
      },
      ai: {
        description: 'Background fill color of badge label chips on menu items',
      },
    },
  },
  badgeBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-paragraph-2-color'), 0.1),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 0.1,
      },
      ai: {
        description: 'Border color of badge label chips on menu items',
      },
    },
  },
  badgeBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description: 'Border thickness (px) of badge label chips on menu items',
      },
    },
  },
  badgeCornerRadiusTopLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Top-left corner radius (px) of badge label chips on menu items',
      },
    },
  },
  badgeCornerRadiusTopRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Top-right corner radius (px) of badge label chips on menu items',
      },
    },
  },
  badgeCornerRadiusBottomLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Bottom-left corner radius (px) of badge label chips on menu items',
      },
    },
  },
  badgeCornerRadiusBottomRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: { type: 'static', value: 20 },
      ai: {
        description:
          'Bottom-right corner radius (px) of badge label chips on menu items',
      },
    },
  },
  badgeCornerRadiusLinked: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description:
          '1 = all badge corner radii locked together, 0 = each corner independent',
      },
    },
  },
});
